import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import {
  DRAFTS_HOME_PATH,
  MATTER_HOME_PATH,
  SEARCH_HOME_PATH,
  TEAM_HOME_PATH,
  getPrimarySidebarNavItems,
  isPrimaryNavItemActive,
} from '@/lib/sidebarNav';
import { API } from '@/lib/api';
import { useResizablePanel } from '@/hooks/useResizablePanel';
import EarlyBirdModal from '@/components/EarlyBirdModal';
import { Plus, Shield, ChevronDown, ChevronUp, X } from 'lucide-react';

const adminNavItem = { path: '/admin', icon: Shield, label: 'Admin' };
const SIDEBAR_WIDTH_STORAGE_KEY = 'advoq_sidebar_width_v1';
const RECENT_TAB_HISTORY_STORAGE_KEY = 'advoq_recent_tab_history_v1';
const DEFAULT_SIDEBAR_WIDTH = 236;
const MIN_SIDEBAR_WIDTH = 208;
const MAX_SIDEBAR_WIDTH = 320;
const RECENT_WORK_LIMIT = 10;

const CHAT_MODE_META = {
  RESEARCH: {
    category: 'Research',
    fallback: 'Pull authorities and continue the legal research trail.',
    contextualLabel: 'Research note',
  },
  DRAFT: {
    category: 'Drafting',
    fallback: 'Continue drafting from the current chamber instructions.',
    contextualLabel: 'Draft around',
  },
  EXPLAIN: {
    category: 'Quick Explain',
    fallback: 'Explain the issue in cleaner chamber-ready language.',
    contextualLabel: 'Explain',
  },
  STRATEGY: {
    category: 'Sabha',
    fallback: 'Tighten the hearing strategy and likely pushback.',
    contextualLabel: 'Sabha for',
  },
  MOOT_COURT: {
    category: 'Sabha',
    fallback: 'Stress-test the argument and likely counterpoints.',
    contextualLabel: 'Sabha for',
  },
  EXAM_PREP: {
    category: 'Prep',
    fallback: 'Review the issue set and revise the legal position.',
    contextualLabel: 'Prep note for',
  },
  CHAT: {
    category: 'Chat',
    fallback: 'Resume the chamber conversation.',
    contextualLabel: 'Continue work on',
  },
};

const JOLLY_MODE_META = {
  brief_me: { category: 'Jolly', fallback: 'Brief the senior from your latest spoken notes.' },
  client_call: { category: 'Jolly', fallback: 'Structure the latest client call into usable notes.' },
  dictation: { category: 'Jolly', fallback: 'Turn dictation into a cleaner chamber working note.' },
  post_hearing_debrief: { category: 'Jolly', fallback: 'Capture the hearing and next filing steps.' },
};

const SAVED_TYPE_META = {
  DOCUMENT: { category: 'Saved', fallback: 'Saved drafting material' },
  RESEARCH: { category: 'Saved', fallback: 'Saved research note' },
  CASE_LAW: { category: 'Saved', fallback: 'Saved authority note' },
  CHAT: { category: 'Saved', fallback: 'Saved conversation note' },
};

const PRIMARY_SECTION_TITLE = 'Navigate';

function isTeamRoutePath(path) {
  const normalizedPath = String(path || '');
  return (
    normalizedPath === TEAM_HOME_PATH
    || normalizedPath.startsWith('/team/')
    || normalizedPath.startsWith('/team-ops')
    || normalizedPath.startsWith('/client-loop')
  );
}

function safeParseJson(raw) {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function parseDate(value) {
  const time = Date.parse(value || '');
  return Number.isFinite(time) ? time : 0;
}

function trimTitle(value, maxLength = 72) {
  const clean = String(value || '').replace(/\s+/g, ' ').trim();
  if (!clean) return '';
  return clean.length > maxLength ? `${clean.slice(0, maxLength - 1).trim()}…` : clean;
}

function formatRelativeTime(value) {
  const timestamp = parseDate(value);
  if (!timestamp) return '';
  const diff = Date.now() - timestamp;
  if (diff < 60 * 1000) return 'Just now';
  if (diff < 60 * 60 * 1000) return `${Math.max(1, Math.round(diff / (60 * 1000)))}m ago`;
  if (diff < 24 * 60 * 60 * 1000) return `${Math.max(1, Math.round(diff / (60 * 60 * 1000)))}h ago`;
  return `${Math.max(1, Math.round(diff / (24 * 60 * 60 * 1000)))}d ago`;
}

function titleLooksGeneric(value) {
  const normalized = String(value || '').trim().toLowerCase();
  return !normalized || normalized === 'new chat' || normalized === 'untitled' || normalized === 'untitled session';
}

function formatEnumLabel(value) {
  return String(value || '')
    .toLowerCase()
    .split('_')
    .filter(Boolean)
    .map((chunk) => chunk.charAt(0).toUpperCase() + chunk.slice(1))
    .join(' ');
}

function summarizeChatTitle(chat) {
  const modeMeta = CHAT_MODE_META[chat?.mode] || CHAT_MODE_META.CHAT;
  if (!titleLooksGeneric(chat?.title)) return trimTitle(chat.title);
  const contextLabel = trimTitle(chat?.matter_name || chat?.linked_doc_title);
  if (contextLabel) return trimTitle(`${modeMeta.contextualLabel} ${contextLabel}`);
  return modeMeta.fallback;
}

function summarizeDocumentTitle(document) {
  if (!titleLooksGeneric(document?.title)) return trimTitle(document.title);
  const docType = formatEnumLabel(document?.type) || 'Draft';
  return trimTitle(`${docType} working draft`);
}

function summarizeMatterTitle(matter) {
  const matterTitle = trimTitle(matter?.title || matter?.name);
  if (matterTitle) return matterTitle;
  return 'Matter workspace';
}

function summarizeJollyTitle(session) {
  if (!titleLooksGeneric(session?.title)) return trimTitle(session.title);
  const modeMeta = JOLLY_MODE_META[session?.mode] || { fallback: 'Voice briefing session' };
  const matterLabel = trimTitle(session?.matter_name);
  if (matterLabel) return trimTitle(`${modeMeta.fallback.replace(/\.$/, '')} for ${matterLabel}.`);
  return modeMeta.fallback;
}

function summarizeAinaTitle(run) {
  const scopeLabel = trimTitle(run?.scope_label || run?.draft_title || run?.matter_name);
  if (scopeLabel) return trimTitle(`Aina mirror for ${scopeLabel}`);
  return 'Aina adversarial pass';
}

function summarizeSavedTitle(item) {
  if (!titleLooksGeneric(item?.title)) return trimTitle(item.title);
  const meta = SAVED_TYPE_META[item?.type] || { fallback: 'Saved work note' };
  return meta.fallback;
}

function buildRouteHistoryKey(userId) {
  return `${RECENT_TAB_HISTORY_STORAGE_KEY}_${userId || 'guest'}`;
}

function buildChatHref(chat) {
  const params = new URLSearchParams();
  if (chat?.workspace_id) params.set('workspace_id', chat.workspace_id);
  const query = params.toString();
  return `/chat/${chat.chat_id}${query ? `?${query}` : ''}`;
}

function getRecentEntryDedupeKey(entry) {
  if (!entry) return '';
  if (entry.kind !== 'route') return `${entry.kind}:${entry.resourceId || entry.href}`;

  const [path = '', search = ''] = String(entry.href || '').split('?');
  const params = new URLSearchParams(search);
  const chatMatch = path.match(/^\/chat\/([^/?#]+)/);
  if (chatMatch) return `chat:${chatMatch[1]}`;

  const documentMatch = path.match(/^\/documents\/([^/?#]+)/);
  if (documentMatch) return `document:${documentMatch[1]}`;

  const matterMatch = path.match(/^\/war-room\/([^/?#]+)/);
  if (matterMatch) return `matter:${matterMatch[1]}`;

  const canonicalMatterMatch = path.match(/^\/matters\/([^/?#]+)$/);
  if (canonicalMatterMatch && canonicalMatterMatch[1] !== 'dashboard') {
    return `matter:${canonicalMatterMatch[1]}`;
  }

  const caseMatch = path.match(/^\/case\/([^/?#]+)/);
  if (caseMatch) return `case:${caseMatch[1]}`;

  if (path === '/jolly' && params.get('session_id')) return `jolly:${params.get('session_id')}`;
  if ((path === '/aina' || path.startsWith('/aina/')) && params.get('run_id')) return `aina:${params.get('run_id')}`;
  const ainaMatterMatch = path.match(/^\/aina\/([^/?#]+)/);
  if (ainaMatterMatch) return `aina-matter:${ainaMatterMatch[1]}`;
  if ((path === '/chat' || path === '/research') && params.get('workspace_id')) {
    return `${path}:${params.get('workspace_id')}`;
  }

  return `route:${entry.href}`;
}

function buildRouteVisit(location) {
  const { pathname, search } = location;
  if (!pathname || pathname === '/' || pathname === '/auth' || pathname.startsWith('/share/')) return null;

  if (pathname === MATTER_HOME_PATH || pathname === '/matters/dashboard') {
    return {
      key: `route:${pathname}${search || ''}`,
      href: `${pathname}${search || ''}`,
      category: 'Matters',
      title: 'Matter home',
      meta: 'Matter-first workspace',
      updatedAt: new Date().toISOString(),
      kind: 'route',
      routePath: pathname,
    };
  }

  if (pathname.startsWith('/today') || pathname.startsWith('/brief')) {
    return {
      key: `route:${pathname}${search || ''}`,
      href: `${pathname}${search || ''}`,
      category: 'Matters',
      title: 'Matter home',
      meta: 'Resume the next move',
      updatedAt: new Date().toISOString(),
      kind: 'route',
      routePath: pathname,
    };
  }

  if (pathname.startsWith('/board')) {
    return {
      key: `route:${pathname}${search || ''}`,
      href: `${pathname}${search || ''}`,
      category: 'Board',
      title: 'Board and court watch',
      meta: 'Court-facing workspace',
      updatedAt: new Date().toISOString(),
      kind: 'route',
      routePath: pathname,
    };
  }

  if (pathname.startsWith(SEARCH_HOME_PATH) || pathname.startsWith('/authorities') || pathname.startsWith('/research')) {
    return {
      key: `route:${pathname}${search || ''}`,
      href: `${pathname}${search || ''}`,
      category: 'Search',
      title: 'Search workspace',
      meta: 'Verified public law',
      updatedAt: new Date().toISOString(),
      kind: 'route',
      routePath: pathname,
    };
  }

  if (pathname.startsWith('/chat')) {
    return {
      key: `route:${pathname}${search || ''}`,
      href: `${pathname}${search || ''}`,
      category: 'Chat',
      title: pathname === '/chat' ? 'General chamber conversation' : 'Open conversation thread',
      meta: 'Active workspace',
      updatedAt: new Date().toISOString(),
      kind: 'route',
      routePath: pathname,
    };
  }

  if (pathname.startsWith('/hearings') || pathname.startsWith('/case')) {
    return {
      key: `route:${pathname}${search || ''}`,
      href: `${pathname}${search || ''}`,
      category: 'Sabha',
      title: pathname === '/case' || pathname === '/hearings' ? 'Sabha workspace' : 'Open Sabha matter prep',
      meta: 'Active workspace',
      updatedAt: new Date().toISOString(),
      kind: 'route',
      routePath: pathname,
    };
  }

  if (pathname.startsWith(DRAFTS_HOME_PATH) || pathname.startsWith('/drafting') || pathname.startsWith('/documents')) {
    return {
      key: `route:${pathname}${search || ''}`,
      href: `${pathname}${search || ''}`,
      category: 'Drafts',
      title: pathname === '/documents' || pathname === '/drafting' ? 'Draft workspace' : 'Working draft file',
      meta: 'Active workspace',
      updatedAt: new Date().toISOString(),
      kind: 'route',
      routePath: pathname,
    };
  }

  if (
    pathname.startsWith('/war-room')
    || /^\/matters\/[^/]+$/.test(pathname)
  ) {
    return {
      key: `route:${pathname}`,
      href: pathname,
      category: 'Matters',
      title: pathname === '/war-room' ? 'Matter control room' : 'Matter workspace',
      meta: 'Active workspace',
      updatedAt: new Date().toISOString(),
      kind: 'route',
      routePath: pathname,
    };
  }

  if (pathname.startsWith('/voice-notes') || pathname.startsWith('/jolly')) {
    return {
      key: `route:${pathname}${search || ''}`,
      href: `${pathname}${search || ''}`,
      category: 'Jolly',
      title: 'Jolly workspace',
      meta: 'Active workspace',
      updatedAt: new Date().toISOString(),
      kind: 'route',
      routePath: pathname,
    };
  }

  if (pathname.startsWith('/red-team') || pathname.startsWith('/aina')) {
    return {
      key: `route:${pathname}${search || ''}`,
      href: `${pathname}${search || ''}`,
      category: 'Aina',
      title: pathname === '/aina' || pathname === '/red-team' ? 'Aina workspace' : 'Matter adversarial mirror',
      meta: 'Active workspace',
      updatedAt: new Date().toISOString(),
      kind: 'route',
      routePath: pathname,
    };
  }

  if (pathname.startsWith(TEAM_HOME_PATH) || pathname.startsWith('/team-ops')) {
    return {
      key: `route:${pathname}${search || ''}`,
      href: `${pathname}${search || ''}`,
      category: 'Team',
      title: 'Team workspace',
      meta: 'Active workspace',
      updatedAt: new Date().toISOString(),
      kind: 'route',
      routePath: pathname,
    };
  }

  if (pathname.startsWith('/client-loop')) {
    return {
      key: `route:${pathname}${search || ''}`,
      href: `${pathname}${search || ''}`,
      category: 'Team',
      title: 'Client-safe update desk',
      meta: 'Active workspace',
      updatedAt: new Date().toISOString(),
      kind: 'route',
      routePath: pathname,
    };
  }

  if (pathname.startsWith('/saved')) {
    return {
      key: `route:${pathname}`,
      href: pathname,
      category: 'Saved',
      title: 'Saved notes and authorities',
      meta: 'Active workspace',
      updatedAt: new Date().toISOString(),
      kind: 'route',
      routePath: pathname,
    };
  }

  if (pathname.startsWith('/templates')) {
    return {
      key: `route:${pathname}`,
      href: pathname,
      category: 'Library',
      title: 'Precedents library',
      meta: 'Active workspace',
      updatedAt: new Date().toISOString(),
      kind: 'route',
      routePath: pathname,
    };
  }

  if (pathname.startsWith('/knowledge-base')) {
    return {
      key: `route:${pathname}`,
      href: pathname,
      category: 'Library',
      title: 'Knowledge library',
      meta: 'Active workspace',
      updatedAt: new Date().toISOString(),
      kind: 'route',
      routePath: pathname,
    };
  }

  if (pathname.startsWith('/student-mode')) {
    return {
      key: `route:${pathname}${search || ''}`,
      href: `${pathname}${search || ''}`,
      category: 'Student',
      title: 'Student mode',
      meta: 'Active workspace',
      updatedAt: new Date().toISOString(),
      kind: 'route',
      routePath: pathname,
    };
  }

  if (pathname.startsWith('/knowledge')) {
    return {
      key: `route:${pathname}`,
      href: pathname,
      category: 'Support',
      title: 'Practice profile',
      meta: 'Active workspace',
      updatedAt: new Date().toISOString(),
      kind: 'route',
      routePath: pathname,
    };
  }

  if (pathname.startsWith('/settings')) {
    return {
      key: `route:${pathname}`,
      href: pathname,
      category: 'Settings',
      title: 'Workspace settings',
      meta: 'Active workspace',
      updatedAt: new Date().toISOString(),
      kind: 'route',
      routePath: pathname,
    };
  }

  if (pathname.startsWith('/admin')) {
    return {
      key: `route:${pathname}`,
      href: pathname,
      category: 'Admin',
      title: 'Admin workspace',
      meta: 'Active workspace',
      updatedAt: new Date().toISOString(),
      kind: 'route',
      routePath: pathname,
    };
  }

  return null;
}

function buildRecentWorkEntries({ localVisits, chats, documents, matters, sessions, ainaRuns, savedItems }) {
  const entries = [];

  (localVisits || []).forEach((visit) => {
    entries.push({
      ...visit,
      updatedAt: visit.updatedAt,
      sortTime: parseDate(visit.updatedAt),
    });
  });

  (chats || []).slice(0, 5).forEach((chat) => {
    const modeMeta = CHAT_MODE_META[chat.mode] || CHAT_MODE_META.CHAT;
    const detail = trimTitle(chat.matter_name || chat.linked_doc_title || formatRelativeTime(chat.updated_at));
    entries.push({
      key: `chat:${chat.chat_id}`,
      href: buildChatHref(chat),
      category: modeMeta.category,
      title: summarizeChatTitle(chat),
      meta: detail || 'Conversation thread',
      updatedAt: chat.updated_at,
      kind: 'chat',
      resourceId: chat.chat_id,
      routePath: '/chat',
      sortTime: parseDate(chat.updated_at),
    });
  });

  (documents || []).slice(0, 4).forEach((document) => {
    entries.push({
      key: `document:${document.doc_id}`,
      href: `/documents/${document.doc_id}`,
      category: 'Drafts',
      title: summarizeDocumentTitle(document),
      meta: formatEnumLabel(document.type) || 'Working file',
      updatedAt: document.updated_at,
      kind: 'document',
      resourceId: document.doc_id,
      routePath: '/documents',
      sortTime: parseDate(document.updated_at),
    });
  });

  (matters || []).slice(0, 4).forEach((matter) => {
    const detail = trimTitle(matter.court_forum || matter.case_number || matter.practice_area || 'Matter workspace');
    entries.push({
      key: `matter:${matter.matter_id}`,
      href: `/war-room/${matter.matter_id}`,
      category: 'Matters',
      title: summarizeMatterTitle(matter),
      meta: detail,
      updatedAt: matter.updated_at || matter.latest_activity_at,
      kind: 'matter',
      resourceId: matter.matter_id,
      routePath: '/war-room',
      sortTime: parseDate(matter.updated_at || matter.latest_activity_at),
    });
  });

  (sessions || []).slice(0, 4).forEach((session) => {
    const modeMeta = JOLLY_MODE_META[session.mode] || { category: 'Jolly' };
    entries.push({
      key: `jolly:${session.session_id}`,
      href: `/jolly?session_id=${session.session_id}`,
      category: modeMeta.category,
      title: summarizeJollyTitle(session),
      meta: trimTitle(session.matter_name || formatEnumLabel(session.mode) || 'Voice session'),
      updatedAt: session.updated_at,
      kind: 'jolly',
      resourceId: session.session_id,
      routePath: '/jolly',
      sortTime: parseDate(session.updated_at),
    });
  });

  (ainaRuns || []).slice(0, 4).forEach((run) => {
    const href = run.matter_id
      ? `/aina/${run.matter_id}?run_id=${run.run_id}`
      : `/aina?run_id=${run.run_id}`;
    entries.push({
      key: `aina:${run.run_id}`,
      href,
      category: 'Aina',
      title: summarizeAinaTitle(run),
      meta: trimTitle(run.summary || `${run.finding_count || 0} findings` || 'Adversarial mirror run'),
      updatedAt: run.updated_at || run.created_at,
      kind: 'aina',
      resourceId: run.run_id,
      routePath: run.matter_id ? `/aina/${run.matter_id}` : '/aina',
      sortTime: parseDate(run.updated_at || run.created_at),
    });
  });

  (savedItems || []).slice(0, 3).forEach((item) => {
    const itemMeta = SAVED_TYPE_META[item.type] || { category: 'Saved' };
    entries.push({
      key: `saved:${item.item_id}`,
      href: '/saved',
      category: itemMeta.category,
      title: summarizeSavedTitle(item),
      meta: trimTitle(item.workspace?.label || formatEnumLabel(item.type) || 'Saved item'),
      updatedAt: item.created_at,
      kind: 'saved',
      resourceId: item.item_id,
      routePath: '/saved',
      sortTime: parseDate(item.created_at),
    });
  });

  const validEntries = entries.filter((entry) => entry.title && entry.href);

  // Track the most recent visit time per key (route visits record when user last touched it)
  const maxTimeByKey = new Map();
  validEntries.forEach((entry) => {
    const key = getRecentEntryDedupeKey(entry);
    maxTimeByKey.set(key, Math.max(maxTimeByKey.get(key) || 0, entry.sortTime));
  });

  const deduped = [];
  const seen = new Set();

  // Sort: non-route entries first so API titles win the dedupe slot; then by time desc
  validEntries
    .sort((a, b) => {
      const aRoute = a.kind === 'route' ? 1 : 0;
      const bRoute = b.kind === 'route' ? 1 : 0;
      if (aRoute !== bRoute) return aRoute - bRoute;
      return b.sortTime - a.sortTime;
    })
    .forEach((entry) => {
      const dedupeKey = getRecentEntryDedupeKey(entry);
      if (seen.has(dedupeKey)) return;
      seen.add(dedupeKey);
      // Use the most recent time for this resource (route visit may be newer than API updated_at)
      const bestTime = maxTimeByKey.get(dedupeKey) || entry.sortTime;
      deduped.push({
        ...entry,
        sortTime: bestTime,
        timeLabel: bestTime ? formatRelativeTime(new Date(bestTime).toISOString()) : '',
      });
    });

  return deduped.sort((a, b) => b.sortTime - a.sortTime).slice(0, RECENT_WORK_LIMIT);
}

function isRecentItemActive(item, location) {
  if (!item) return false;
  if (item.kind === 'chat') return location.pathname === `/chat/${item.resourceId}`;
  if (item.kind === 'document') return location.pathname === `/documents/${item.resourceId}`;
  if (item.kind === 'matter') return location.pathname === `/war-room/${item.resourceId}`;
  if (item.kind === 'jolly') return location.pathname === '/jolly' && new URLSearchParams(location.search).get('session_id') === item.resourceId;
  if (item.kind === 'aina') return location.pathname.startsWith('/aina') && new URLSearchParams(location.search).get('run_id') === item.resourceId;
  if (item.kind === 'saved') return location.pathname.startsWith('/saved');
  return location.pathname === item.routePath || `${location.pathname}${location.search || ''}` === item.href;
}

export default function Sidebar({
  collapsed,
  isMobile = false,
  mobileOpen = false,
  onMobileClose,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    user,
    getAuthHeaders,
    hasTeamWorkspace,
    defaultTeamWorkspace,
    setActiveWorkspace,
  } = useAuth();
  const [isAdminOpen, setIsAdminOpen] = useState(false);
  const [isRecentHistoryOpen, setIsRecentHistoryOpen] = useState(true);
  const [showCommunityAccessModal, setShowCommunityAccessModal] = useState(false);
  const [recentWork, setRecentWork] = useState([]);
  const navItems = getPrimarySidebarNavItems({ hasTeamWorkspace });
  const initialSidebarWidth = typeof window !== 'undefined'
    ? Math.min(
        MAX_SIDEBAR_WIDTH,
        Math.max(MIN_SIDEBAR_WIDTH, Number(window.localStorage.getItem(SIDEBAR_WIDTH_STORAGE_KEY)) || DEFAULT_SIDEBAR_WIDTH),
      )
    : DEFAULT_SIDEBAR_WIDTH;
  const { width: sidebarWidth, startDrag: startSidebarDrag } = useResizablePanel(initialSidebarWidth, {
    min: MIN_SIDEBAR_WIDTH,
    max: MAX_SIDEBAR_WIDTH,
  });
  const isDesktopCollapsed = !isMobile && collapsed;
  const isSidebarOpen = isMobile ? mobileOpen : true;

  const closeSidebarOnMobile = () => {
    if (isMobile && isSidebarOpen) {
      onMobileClose?.();
    }
  };

  const handleMobileCloseClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    onMobileClose?.();
  };

  const handleNavigate = (path) => {
    if (isTeamRoutePath(path) && defaultTeamWorkspace) {
      setActiveWorkspace(defaultTeamWorkspace);
    } else if (hasTeamWorkspace) {
      setActiveWorkspace('personal');
    }
    navigate(path);
    closeSidebarOnMobile();
  };

  const handleNewChat = () => handleNavigate('/chat');

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(SIDEBAR_WIDTH_STORAGE_KEY, String(sidebarWidth));
    }
  }, [sidebarWidth]);

  useEffect(() => {
    if (typeof window === 'undefined' || !user?.user_id) return;
    const nextVisit = buildRouteVisit({ pathname: location.pathname, search: location.search });
    if (!nextVisit) return;
    const storageKey = buildRouteHistoryKey(user.user_id);
    const existing = safeParseJson(window.localStorage.getItem(storageKey));
    const deduped = existing.filter((entry) => entry?.href !== nextVisit.href);
    window.localStorage.setItem(storageKey, JSON.stringify([nextVisit, ...deduped].slice(0, 8)));
  }, [location.pathname, location.search, user?.user_id]);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    async function loadRecentWork() {
      if (!user?.user_id) {
        setRecentWork([]);
        return;
      }

      const storageKey = buildRouteHistoryKey(user.user_id);
      const localVisits = typeof window !== 'undefined'
        ? safeParseJson(window.localStorage.getItem(storageKey))
        : [];

      try {
        const headers = getAuthHeaders();
        const responses = await Promise.allSettled([
          fetch(`${API}/chats`, { headers, credentials: 'include', signal: controller.signal }),
          fetch(`${API}/documents`, { headers, credentials: 'include', signal: controller.signal }),
          fetch(`${API}/matters`, { headers, credentials: 'include', signal: controller.signal }),
          fetch(`${API}/jolly/sessions`, { headers, credentials: 'include', signal: controller.signal }),
          fetch(`${API}/aina/runs`, { headers, credentials: 'include', signal: controller.signal }),
          fetch(`${API}/saved`, { headers, credentials: 'include', signal: controller.signal }),
        ]);

        const readPayload = async (result) => {
          if (result.status !== 'fulfilled' || !result.value.ok) return [];
          try {
            return await result.value.json();
          } catch {
            return [];
          }
        };

        const [chats, documents, matters, sessions, ainaRuns, savedItems] = await Promise.all(responses.map(readPayload));
        if (cancelled) return;

        setRecentWork(buildRecentWorkEntries({
          localVisits,
          chats,
          documents,
          matters,
          sessions,
          ainaRuns,
          savedItems,
        }));
      } catch (error) {
        if (cancelled || error?.name === 'AbortError') return;
        setRecentWork(buildRecentWorkEntries({ localVisits, chats: [], documents: [], matters: [], sessions: [], ainaRuns: [], savedItems: [] }));
      }
    }

    loadRecentWork();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [getAuthHeaders, location.pathname, location.search, user?.user_id]);

  const renderNavItems = (items) => (
    <nav className="app-sidebar__nav">
      {items.map(item => {
        const active = isPrimaryNavItemActive(item.path, location.pathname);
        return (
          <button
            key={item.path}
            data-testid={`nav-${item.path.replace(/^\//, '').replace(/\//g, '-')}`}
            onClick={() => handleNavigate(item.path)}
            className={`app-sidebar__nav-item ${active ? 'is-active' : ''}`}
            title={isDesktopCollapsed ? item.label : undefined}
          >
            <item.icon size={18} />
            {!isDesktopCollapsed && <span className="app-sidebar__nav-label">{item.label}</span>}
          </button>
        );
      })}
    </nav>
  );

  return (
    <>
      {isMobile && isSidebarOpen && (
        <button
          type="button"
          aria-label="Close sidebar"
          className="fixed inset-0 z-40 bg-black/40 md:hidden"
          onClick={closeSidebarOnMobile}
        />
      )}

      <div
        aria-hidden="true"
        className={`app-sidebar__spacer hidden md:block ${isDesktopCollapsed ? 'collapsed' : 'open'}`}
        style={{ '--sidebar-width': `${sidebarWidth}px` }}
      />

      <aside
        className={`app-sidebar fixed inset-y-0 left-0 z-50 transform transition-transform md:z-30 ${isDesktopCollapsed ? 'collapsed' : 'open'} ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0`}
        data-testid="app-sidebar"
        style={{
          '--sidebar-width': `${sidebarWidth}px`,
          ...(isMobile ? { width: 'min(288px, 85vw)' } : {}),
        }}
      >
        {!isDesktopCollapsed && !isMobile && (
          <div
            className="app-sidebar__resize-handle hidden md:block"
            onMouseDown={startSidebarDrag}
            title="Resize sidebar"
          />
        )}
        <div className="app-sidebar__inner">
          {isMobile ? (
            <div className="app-sidebar__mobile-bar">
              <span className="app-sidebar__mobile-title">Advoq</span>
              <button
                type="button"
                onClick={handleMobileCloseClick}
                className="app-sidebar__icon-button"
                aria-label="Close menu"
                title="Close menu"
              >
                <X size={16} />
              </button>
            </div>
          ) : null}

          <button
            data-testid="new-chat-btn"
            onClick={handleNewChat}
            className="advoq-btn-primary app-sidebar__new text-sm"
            title="Start a new chat"
          >
            <Plus size={16} />
            {!isDesktopCollapsed && 'New chat'}
          </button>

          <div className="app-sidebar__nav-wrap">
            <div className="app-sidebar__nav-section">
              {!isDesktopCollapsed && <p className="app-sidebar__section-label">{PRIMARY_SECTION_TITLE}</p>}
              {renderNavItems(navItems)}
            </div>

            {user?.admin_console_access && (
              <div className="app-sidebar__nav-section">
                {!isDesktopCollapsed && (
                  <button
                    type="button"
                    onClick={() => setIsAdminOpen((open) => !open)}
                    className="app-sidebar__section-toggle app-sidebar__section-toggle--admin"
                    aria-expanded={isAdminOpen}
                  >
                    <span>Admin</span>
                    {isAdminOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                  </button>
                )}
                {isAdminOpen ? (
                  <nav className="app-sidebar__nav">
                    <button
                      data-testid="nav-admin"
                      onClick={() => handleNavigate('/admin')}
                      className={`app-sidebar__nav-item app-sidebar__nav-item--admin ${location.pathname === '/admin' ? 'is-active' : ''}`}
                      title={isDesktopCollapsed ? adminNavItem.label : undefined}
                    >
                      <Shield size={18} />
                      {!isDesktopCollapsed && <span className="app-sidebar__nav-label">{adminNavItem.label}</span>}
                    </button>
                  </nav>
                ) : null}
              </div>
            )}

            {!isDesktopCollapsed && (
              <div className="app-sidebar__nav-section app-sidebar__nav-section--history">
                <button
                  type="button"
                  onClick={() => setIsRecentHistoryOpen((open) => !open)}
                  className="app-sidebar__section-toggle"
                  aria-expanded={isRecentHistoryOpen}
                >
                  <span>Recent</span>
                  {isRecentHistoryOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                </button>
                {isRecentHistoryOpen ? (
                  <div className="app-sidebar__history-scroll">
                    {recentWork.length ? (
                      <div className="app-sidebar__history-list">
                        {recentWork.map((item) => (
                          <button
                            key={item.key}
                            type="button"
                            onClick={() => handleNavigate(item.href)}
                            className={`app-sidebar__history-item ${isRecentItemActive(item, location) ? 'is-active' : ''}`}
                            title={`${item.category}: ${item.title}`}
                          >
                            <span className="app-sidebar__history-dot" />
                            <span className="app-sidebar__history-title">{item.title}</span>
                            {item.timeLabel ? <span className="app-sidebar__history-time">{item.timeLabel}</span> : null}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <p className="app-sidebar__history-empty">
                        Recent work will show up here.
                      </p>
                    )}
                  </div>
                ) : null}
              </div>
            )}
          </div>

          {!isDesktopCollapsed && String(user?.plan || 'FREE').toUpperCase() === 'FREE' ? (
            <button
              type="button"
              onClick={() => setShowCommunityAccessModal(true)}
              className="app-sidebar__nav-item mb-3"
              style={{ borderStyle: 'dashed' }}
            >
              <Shield size={16} />
              <span className="app-sidebar__nav-label">Unlock Specialists</span>
            </button>
          ) : null}
        </div>
        {showCommunityAccessModal ? (
          <EarlyBirdModal
            onClose={() => setShowCommunityAccessModal(false)}
            onAccessGranted={() => setShowCommunityAccessModal(false)}
          />
        ) : null}
      </aside>
    </>
  );
}
